import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FieldConfigDTO, MoneyDTO } from '../../../../../../../../system/system-dtos';
import { copyObj } from '../../../../../../../utils/utils';
import { MoneyService } from '../../../../../../../data-type-widgets/data-type/shared/money/money.service';
import { CashSummary } from '../app-manual-adjustment-modal.component';

@Component({
  selector: 'app-manual-adjustment-cash',
  templateUrl: './manual-adjustment-cash.component.html',
  styleUrls: ['./manual-adjustment-cash.component.scss'],
  standalone: false,
})
export class ManualAdjustmentCashComponent implements OnInit {
  private readonly EQUALS_ICON = 'fas fa-equals';

  private readonly UP_ICON = 'fas fa-angle-double-up';

  private readonly DOWN_ICON = 'fas fa-angle-double-down';

  @Output() deltaChange: EventEmitter<CashSummary> = new EventEmitter<CashSummary>();

  @Input() data: ManualAdjustmentInput;
  @Input()
  public enabled: boolean = true;

  modeConfig: ManualAdjustmentModeConfig;

  currentConfig: FieldConfigDTO;

  current: MoneyDTO;

  postChangesConfig: FieldConfigDTO;

  postChanges: MoneyDTO;

  deltaConfig: FieldConfigDTO;

  summaryIcon: string = this.EQUALS_ICON;

  summaryClass: string = '';

  formGroup: UntypedFormGroup;

  operation: string = '';

  constructor(
    private moneyService: MoneyService,
    private fb: UntypedFormBuilder
  ) {
    this.currentConfig = this.moneyService.moneyConfig('current');
    this.postChangesConfig = this.moneyService.moneyConfig('postChanges');
    this.deltaConfig = this.moneyService.moneyConfig('delta');
  }

  ngOnInit(): void {
    this.modeConfig = CONFIG_BY_MODE.get(this.data.mode);

    this.current = copyObj(this.data.balance);
    this.postChanges = copyObj(this.data.balance);

    this.formGroup = this.fb.group({
      checked: [{ value: false, disabled: !this.enabled }],
      adjustmentType: ['CREDIT'],
      delta: this.moneyService.zeroMoney(this.data.balance?.currency),
    });

    this.formGroup.valueChanges.subscribe((obj: { checked: boolean; delta: MoneyDTO; adjustmentType: string }) => {
      const deltaWithSign = this.getDeltaWithSignValue(obj.delta.value, obj.adjustmentType);
      this.postChanges = this.moneyService.create(this.current.value + deltaWithSign, obj.delta.currency);
      this.determineSummaryIconAndClass();
      if (obj.checked) {
        this.deltaChange.emit({
          before: this.current,
          delta: this.moneyService.create(deltaWithSign, obj.delta.currency),
          after: this.postChanges,
        });
      } else {
        this.deltaChange.emit(null);
      }
    });
  }

  private getDeltaWithSignValue(value: number, adjustmentType: string): number {
    return adjustmentType === 'CREDIT' ? value : -value;
  }

  private determineSummaryIconAndClass(): void {
    if (this.postChanges.value > this.current.value) {
      this.summaryIcon = this.UP_ICON;
      this.summaryClass = 'delta-positive';
      this.operation = '(CREDIT)';
    } else if (this.postChanges.value < this.current.value) {
      this.summaryIcon = this.DOWN_ICON;
      this.summaryClass = 'delta-negative';
      this.operation = '(DEBIT)';
    } else {
      this.summaryIcon = this.EQUALS_ICON;
      this.summaryClass = '';
      this.operation = '';
    }
  }

  public ngOnChanges(): void {
    if (this.enabled) {
      this.formGroup?.get('checked').enable();
    } else {
      this.formGroup?.get('checked')?.setValue(false);
      this.formGroup?.get('checked').disable();
    }
  }
}

export enum ManualAdjustmentMode {
  CASH,
  RESERVE,
  BONUS,
}

export interface ManualAdjustmentInput {
  balance: MoneyDTO;
  mode: ManualAdjustmentMode;
  note: string;
}

export interface ManualAdjustmentModeConfig {
  title: string;
  balanceLabel: string;
  adjustmentLabel: string;
  newCashLabelLabel: string;
}

const CONFIG_BY_MODE: Map<ManualAdjustmentMode, ManualAdjustmentModeConfig> = new Map<
  ManualAdjustmentMode,
  ManualAdjustmentModeConfig
>([
  [
    ManualAdjustmentMode.CASH,
    {
      balanceLabel: 'Cash balance',
      adjustmentLabel: 'Adjustment',
      newCashLabelLabel: 'New cash balance',
      title: 'CASH Balance Adjustment',
    },
  ],
  [
    ManualAdjustmentMode.RESERVE,
    {
      balanceLabel: 'Reserve balance',
      adjustmentLabel: 'Adjustment',
      newCashLabelLabel: 'New reserve balance',
      title: 'RESERVE Balance Adjustment',
    },
  ],
  [
    ManualAdjustmentMode.BONUS,
    {
      balanceLabel: 'Bonus balance',
      adjustmentLabel: 'Adjustment',
      newCashLabelLabel: 'New bonus balance',
      title: 'BONUS Balance Adjustment',
    },
  ],
]);
